import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Building2,
  Map,
  Wrench,
  AlertTriangle,
  DollarSign,
  Plus,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Droplets,
  Zap,
  Route,
  AlertCircle,
  CheckCircle2,
  Clock,
  MapPin,
  Loader2,
  Sparkles,
  FileText,
  Megaphone,
  HardHat,
  Snowflake
} from "lucide-react";

const MetroDistrictPage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [projects, setProjects] = useState([]);
  const [issues, setIssues] = useState([]);
  const [gisLayers, setGisLayers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showAiExplain, setShowAiExplain] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  
  // Forms
  const [projectForm, setProjectForm] = useState({
    title: "",
    description: "",
    category: "roads",
    budget: ""
  });
  
  const [issueForm, setIssueForm] = useState({
    title: "",
    description: "",
    category: "road_damage",
    location_description: ""
  });

  const isAdmin = user?.role && ["admin", "property_manager", "metro_admin"].includes(user.role);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, projectsRes, issuesRes, layersRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/metro/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/metro/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/metro/issues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/metro/gis/layers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/metro/announcements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { announcements: [] } }))
      ]);
      setDashboard(dashRes.data);
      setProjects(projectsRes.data.projects || []);
      setIssues(issuesRes.data.issues || []);
      setGisLayers(layersRes.data.layers || []);
      setAnnouncements(announcementsRes.data.announcements || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      const params = new URLSearchParams({
        ...projectForm,
        budget: projectForm.budget || "0"
      });
      await axios.post(`${API}/metro/projects?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowProjectDialog(false);
      setProjectForm({ title: "", description: "", category: "roads", budget: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create project");
    }
  };

  const handleReportIssue = async () => {
    try {
      const params = new URLSearchParams(issueForm);
      await axios.post(`${API}/metro/issues?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowIssueDialog(false);
      setIssueForm({ title: "", description: "", category: "road_damage", location_description: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to report issue");
    }
  };

  const handleAiExplain = async (topic) => {
    setAiLoading(true);
    setShowAiExplain(true);
    try {
      const res = await axios.post(`${API}/metro/ai/explain-hoa-vs-metro?topic=${encodeURIComponent(topic)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiExplanation(res.data.explanation);
    } catch (error) {
      setAiExplanation("Unable to generate explanation. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      proposed: "bg-gray-100 text-gray-800",
      approved: "bg-blue-100 text-blue-800",
      in_progress: "bg-amber-100 text-amber-800",
      completed: "bg-green-100 text-green-800",
      archived: "bg-gray-100 text-gray-500",
      submitted: "bg-blue-100 text-blue-800",
      under_review: "bg-purple-100 text-purple-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-500"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      roads: <Route className="w-5 h-5" />,
      water: <Droplets className="w-5 h-5" />,
      sewer: <Building2 className="w-5 h-5" />,
      drainage: <Droplets className="w-5 h-5" />,
      snow_removal: <Snowflake className="w-5 h-5" />,
      lighting: <Zap className="w-5 h-5" />,
      road_damage: <AlertTriangle className="w-5 h-5" />,
      drainage_issue: <Droplets className="w-5 h-5" />,
      utility_concern: <Zap className="w-5 h-5" />
    };
    return icons[category] || <Wrench className="w-5 h-5" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="metro-district-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/board">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0066CC] to-[#004499] rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-xl font-semibold">Metro District 2.0</h1>
                <p className="text-sm text-muted-foreground">Infrastructure, utilities, and district management</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Report Issue
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Report Metro Issue</DialogTitle>
                  <DialogDescription>Report infrastructure or utility issues</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Issue Title</Label>
                    <Input
                      value={issueForm.title}
                      onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                      placeholder="Pothole on Main Street"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={issueForm.category} onValueChange={(v) => setIssueForm({ ...issueForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="road_damage">Road Damage</SelectItem>
                        <SelectItem value="drainage_issue">Drainage Issue</SelectItem>
                        <SelectItem value="water_pressure">Water Pressure</SelectItem>
                        <SelectItem value="snow_removal">Snow Removal</SelectItem>
                        <SelectItem value="utility_concern">Utility Concern</SelectItem>
                        <SelectItem value="signage">Signage</SelectItem>
                        <SelectItem value="lighting">Lighting</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={issueForm.location_description}
                      onChange={(e) => setIssueForm({ ...issueForm, location_description: e.target.value })}
                      placeholder="Near main entrance gate"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={issueForm.description}
                      onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                      placeholder="Describe the issue..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowIssueDialog(false)}>Cancel</Button>
                  <Button onClick={handleReportIssue} className="bg-[#0066CC] hover:bg-[#004499]">Submit Report</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {isAdmin && (
              <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0066CC] hover:bg-[#004499]">
                    <Plus className="w-4 h-4 mr-2" /> New Project
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Metro Project</DialogTitle>
                    <DialogDescription>Add a new infrastructure project</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Project Title</Label>
                      <Input
                        value={projectForm.title}
                        onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                        placeholder="Road Resurfacing - Phase 1"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={projectForm.category} onValueChange={(v) => setProjectForm({ ...projectForm, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="roads">Roads</SelectItem>
                          <SelectItem value="water">Water</SelectItem>
                          <SelectItem value="sewer">Sewer</SelectItem>
                          <SelectItem value="drainage">Drainage</SelectItem>
                          <SelectItem value="landscaping">Landscaping</SelectItem>
                          <SelectItem value="snow_removal">Snow Removal</SelectItem>
                          <SelectItem value="lighting">Lighting</SelectItem>
                          <SelectItem value="signage">Signage</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Budget</Label>
                      <Input
                        type="number"
                        value={projectForm.budget}
                        onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                        placeholder="150000"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={projectForm.description}
                        onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                        placeholder="Project details..."
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateProject} className="bg-[#0066CC] hover:bg-[#004499]">Create Project</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 bg-white border mb-6">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#0066CC] data-[state=active]:text-white">Dashboard</TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-[#0066CC] data-[state=active]:text-white">Projects</TabsTrigger>
            <TabsTrigger value="issues" className="data-[state=active]:bg-[#0066CC] data-[state=active]:text-white">Issues</TabsTrigger>
            <TabsTrigger value="gis" className="data-[state=active]:bg-[#0066CC] data-[state=active]:text-white">GIS Map</TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-[#0066CC] data-[state=active]:text-white">Announcements</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <HardHat className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.active_projects || 0}</p>
                      <p className="text-xs text-muted-foreground">Active Projects</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{dashboard?.open_issues || 0}</p>
                      <p className="text-xs text-muted-foreground">Open Issues</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatCurrency(dashboard?.budget_summary?.total_budget)}</p>
                      <p className="text-xs text-muted-foreground">Total Budget</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatCurrency(dashboard?.budget_summary?.remaining)}</p>
                      <p className="text-xs text-muted-foreground">Remaining</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Budget Progress */}
            {dashboard?.budget_summary && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Budget Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Budget Spent</span>
                        <span>{formatCurrency(dashboard.budget_summary.total_spent)} of {formatCurrency(dashboard.budget_summary.total_budget)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-[#0066CC] h-3 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (dashboard.budget_summary.total_spent / dashboard.budget_summary.total_budget) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Help Card */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-[#0066CC] to-[#004499] text-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  HOA vs Metro District
                </CardTitle>
                <CardDescription className="text-white/70">Understand responsibilities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {["Roads & Streets", "Water & Sewer", "Snow Removal", "Landscaping"].map((topic) => (
                    <Button
                      key={topic}
                      variant="secondary"
                      className="bg-white/10 hover:bg-white/20 text-white border-0"
                      onClick={() => handleAiExplain(topic)}
                    >
                      {topic}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Projects</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_projects?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_projects.slice(0, 3).map((p) => (
                        <div key={p.project_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                              {getCategoryIcon(p.category)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{p.title}</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(p.budget)}</p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(p.status)}>{p.status?.replace(/_/g, " ")}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No recent projects.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Issues</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_issues?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_issues.slice(0, 3).map((i) => (
                        <div key={i.issue_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600">
                              {getCategoryIcon(i.category)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{i.title}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(i.created_at)}</p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(i.status)}>{i.status?.replace(/_/g, " ")}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No recent issues.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            {projects.length > 0 ? projects.map((p) => (
              <Card key={p.project_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                        {getCategoryIcon(p.category)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getStatusColor(p.status)}>{p.status?.replace(/_/g, " ")}</Badge>
                          <Badge variant="outline" className="capitalize">{p.category?.replace(/_/g, " ")}</Badge>
                        </div>
                        <h3 className="font-medium">{p.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                        {p.contractor && (
                          <p className="text-sm text-muted-foreground mt-2">Contractor: {p.contractor}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(p.budget)}</p>
                      {p.actual_cost > 0 && (
                        <p className="text-sm text-muted-foreground">Spent: {formatCurrency(p.actual_cost)}</p>
                      )}
                      {p.start_date && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {formatDate(p.start_date)} - {p.end_date ? formatDate(p.end_date) : "Ongoing"}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <HardHat className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No projects found.</p>
              </div>
            )}
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            {issues.length > 0 ? issues.map((i) => (
              <Card key={i.issue_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600">
                        {getCategoryIcon(i.category)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getStatusColor(i.status)}>{i.status?.replace(/_/g, " ")}</Badge>
                          <Badge variant="outline" className="capitalize">{i.category?.replace(/_/g, " ")}</Badge>
                        </div>
                        <h3 className="font-medium">{i.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{i.description}</p>
                        {i.location_description && (
                          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {i.location_description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <p>Reported: {formatDate(i.created_at)}</p>
                      {i.resolved_at && (
                        <p className="text-green-600">Resolved: {formatDate(i.resolved_at)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-500" />
                <p>No issues reported.</p>
              </div>
            )}
          </TabsContent>

          {/* GIS Tab */}
          <TabsContent value="gis" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Map className="w-5 h-5" />
                  GIS Utility Layers
                </CardTitle>
                <CardDescription>Infrastructure mapping and asset tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {gisLayers.map((layer) => (
                    <div
                      key={layer.type}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      style={{ borderLeftColor: layer.color, borderLeftWidth: "4px" }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: layer.color }}
                        />
                        <span className="font-medium text-sm">{layer.name}</span>
                      </div>
                      <p className="text-2xl font-bold">{layer.asset_count}</p>
                      <p className="text-xs text-muted-foreground">assets</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-8 bg-gray-100 rounded-lg text-center">
                  <Map className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-muted-foreground">Interactive GIS map coming soon</p>
                  <p className="text-sm text-muted-foreground mt-1">View and manage infrastructure assets</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="space-y-4">
            {announcements.length > 0 ? announcements.map((a) => (
              <Card key={a.announcement_id} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Megaphone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={a.priority === "urgent" ? "bg-red-100 text-red-800" : a.priority === "high" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}>
                          {a.priority}
                        </Badge>
                        <span className="text-sm text-muted-foreground">{formatDate(a.published_at)}</span>
                      </div>
                      <h3 className="font-medium">{a.title}</h3>
                      <p className="text-sm text-muted-foreground mt-2">{a.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-muted-foreground">
                <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No Metro District announcements.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* AI Explanation Dialog */}
      <Dialog open={showAiExplain} onOpenChange={setShowAiExplain}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              HOA vs Metro District Responsibilities
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {aiLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0066CC]" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm">{aiExplanation}</pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </AdminSidebar>
  );
};

export default MetroDistrictPage;
